import * as amplitude from '@amplitude/analytics-browser';
import { REACT_APP_AMPLITUDE_API_KEY, REACT_APP_COUNTRY, REACT_APP_ENVIRONMENT } from '../config';

let amplitudeInitialized = false;

const QUOTE_ID_PATTERN = /([?&]quoteID=)[^&#]+/gi;

const redactQuoteId = (value: unknown): unknown =>
  typeof value === 'string' ? value.replace(QUOTE_ID_PATTERN, '$1REDACTED') : value;

const redactRecord = (record: Record<string, unknown> | undefined): void => {
  if (!record) {
    return;
  }
  Object.keys(record).forEach((key) => {
    const value = record[key];
    if (Array.isArray(value)) {
      record[key] = value.map(redactQuoteId);
    } else if (value && typeof value === 'object') {
      redactRecord(value as Record<string, unknown>);
    } else {
      record[key] = redactQuoteId(value);
    }
  });
};

export const sanitizePayload = <T extends { events?: readonly unknown[] }>(payload: T): T => {
  payload.events?.forEach((event) => redactRecord(event as Record<string, unknown>));
  return payload;
};

// La redacción vive en el TRANSPORTE (último eslabón antes del envío), no en la
// cadena de enrichment: los plugins de autocapture se registran async después del
// init y agregan properties con URLs crudas, así que ningún enrichment puede
// garantizar correr último. El setup() del plugin recibe el config y envuelve el
// transportProvider real.
export const quoteIdSanitizerPlugin = (): amplitude.Types.EnrichmentPlugin => ({
  name: 'quote-id-sanitizer',
  type: 'enrichment',
  setup: async (config) => {
    const provider = config.transportProvider;
    config.transportProvider = {
      send: (serverUrl, payload) => provider.send(serverUrl, sanitizePayload(payload))
    };
  },
  execute: async (event) => event
});

// Atributos que pueden repetir el texto visible de una opción (p.ej. `title={option.label}`
// en las pills del chat). `id` y `class` no se pueden enmascarar (el plugin los necesita).
const MASKED_ATTRIBUTES = 'title,aria-label,alt,placeholder';

/**
 * La app entera muestra respuestas y datos personales (form, chat, summary, confirmación,
 * anexos, firma), y el autocapture manda el texto del elemento clickeado y sus atributos.
 * La máscara va en `<body>`: el plugin mira `data-amp-mask` en cualquier ancestro (texto →
 * `*****`) y junta `data-amp-mask-attributes` de los ancestros debajo de `<html>`. El click se
 * sigue contando (tag, id, clases, test-ids), solo sin el texto.
 */
export const maskAutocapturedContent = (root: HTMLElement | null = document.body): void => {
  if (!root) {
    return;
  }
  root.setAttribute('data-amp-mask', '');
  root.setAttribute('data-amp-mask-attributes', MASKED_ATTRIBUTES);
};

export const initAmplitude = (): void => {
  if (!REACT_APP_AMPLITUDE_API_KEY || amplitudeInitialized) {
    return;
  }

  maskAutocapturedContent();
  amplitude.init(REACT_APP_AMPLITUDE_API_KEY, {
    autocapture: {
      elementInteractions: true,
      pageViews: {
        trackHistoryChanges: 'pathOnly'
      }
    }
  });
  amplitude.add(quoteIdSanitizerPlugin());

  const identify = new amplitude.Identify();
  identify.set('market', REACT_APP_COUNTRY);
  identify.set('environment', REACT_APP_ENVIRONMENT);
  amplitude.identify(identify);

  amplitudeInitialized = true;
};

export const isAmplitudeInitialized = (): boolean => amplitudeInitialized;

export type ApplicationExperience = 'legacy' | 'wizard' | 'chat';

// El quoteID es una credencial: en el storage del navegador solo queda un hash corto, nunca
// el token. Alcanza para asociar la experiencia y el `signed` a SU solicitud.
const quoteKey = (quoteId: string): string => {
  let hash = 5381;
  for (let i = 0; i < quoteId.length; i += 1) {
    hash = ((hash << 5) + hash + quoteId.charCodeAt(i)) >>> 0;
  }
  return hash.toString(36);
};

const EXPERIENCE_KEY_PREFIX = 'amp_exp_';
const SIGNED_KEY_PREFIX = 'amp_signed_';

const readStorage = (key: string): string | null => {
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
};

const writeStorage = (key: string, value: string): void => {
  try {
    window.localStorage.setItem(key, value);
  } catch {
    // Storage bloqueado (modo privado, cookies off): el evento sale igual, sin la asociación.
  }
};

const experienceFor = (quoteId?: string): ApplicationExperience | undefined => {
  if (!quoteId) {
    return undefined;
  }
  const stored = readStorage(`${EXPERIENCE_KEY_PREFIX}${quoteKey(quoteId)}`);
  return stored === 'legacy' || stored === 'wizard' || stored === 'chat' ? stored : undefined;
};

/**
 * Evento del funnel por status. La experiencia va como PROPIEDAD DEL EVENTO, resuelta por
 * solicitud: una user property quedaría pegada al dispositivo y atribuiría a `chat` los pasos
 * de otra cotización abierta después por link directo. Sin experiencia conocida (p.ej. se
 * entró por /logalty sin pasar por el gate) el evento sale sin ella, no con una adivinada.
 */
export const trackApplicationStep = (status: string, quoteId?: string): void => {
  if (!amplitudeInitialized) {
    return;
  }
  const experience = experienceFor(quoteId);
  amplitude.track('Application Step Completed', {
    status,
    market: REACT_APP_COUNTRY,
    ...(experience ? { experience } : {})
  });
};

// Una entrada por solicitud por carga de página: el gate se remonta al volver del summary a
// editar un paso (otra Route) y re-corre al cambiar los search params.
const openedThisPage = new Set<string>();

export const trackApplicationOpened = (input: {
  quoteId: string;
  experience: ApplicationExperience;
  resumed: boolean;
}): void => {
  if (!amplitudeInitialized) {
    return;
  }
  const key = quoteKey(input.quoteId);
  writeStorage(`${EXPERIENCE_KEY_PREFIX}${key}`, input.experience);
  if (openedThisPage.has(key)) {
    return;
  }
  openedThisPage.add(key);
  amplitude.track('Application Opened', {
    experience: input.experience,
    resumed: input.resumed,
    market: REACT_APP_COUNTRY
  });
};

/**
 * `signed` es el paso terminal del funnel, pero el retome lo detecta en CADA reingreso a una
 * solicitud firmada. Se manda una vez por solicitud en este navegador; devuelve si salió.
 */
export const trackApplicationSigned = (quoteId: string): boolean => {
  if (!amplitudeInitialized) {
    return false;
  }
  const key = `${SIGNED_KEY_PREFIX}${quoteKey(quoteId)}`;
  if (readStorage(key)) {
    return false;
  }
  writeStorage(key, '1');
  trackApplicationStep('signed', quoteId);
  return true;
};

export const trackIntakeStepCompleted = (input: {
  stepId: string;
  stepNumber: number;
  totalSteps: number;
  schemaVersion?: string;
}): void => {
  if (!amplitudeInitialized) {
    return;
  }
  amplitude.track('Intake Step Completed', {
    step_id: input.stepId,
    step_number: input.stepNumber,
    total_steps: input.totalSteps,
    schema_version: input.schemaVersion,
    experience: 'wizard',
    market: REACT_APP_COUNTRY
  });
};

// Espera acotada: garantiza el envío del evento terminal antes de un hard-redirect
// (el unload aborta requests en vuelo) sin retener al usuario más de maxWaitMs.
export const flushAmplitude = async (maxWaitMs = 300): Promise<void> => {
  if (!amplitudeInitialized) {
    return;
  }
  await Promise.race([
    amplitude.flush().promise,
    new Promise<void>((resolve) => setTimeout(resolve, maxWaitMs))
  ]);
};

export const resetAmplitudeForTests = (): void => {
  amplitudeInitialized = false;
  openedThisPage.clear();
};
